<template>
    <v-dialog v-if="dialog" v-model="dialog" width="800" @keydown.esc="cancel(true)" @click:outside="cancel(true)">
        <v-card>
            <v-toolbar dark dense flat>
                <v-toolbar-title color="#2B3848" class="text-body-1 font-weight-bold white--text d-flex align-center" style="width: 100%">
                    <span>{{ title }}</span>
                    <v-spacer />
                    <v-btn icon color="red" @click="cancel(false)">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar-title>
            </v-toolbar>
            <div class="d-flex align-start pt-4">
                <v-icon v-if="icon" :color="iconColor" size="64" class="px-4 pt-8">{{ icon }}</v-icon>
                <v-card-text class="text-body-1 pa-2">
                    <div class="d-flex flex-column flex-grow-1">
                        <v-card-text v-show="!!message" class="pa-4 black--text text-body-1" v-html="message" />

                        <v-card-text v-if="warningMessage" class="pl-4 pt-0 text-body-1">
                            <v-checkbox v-model="warningMessageChecked" hide-details>
                                <template v-slot:label>
                                    <span class="red--text text--lighten-2" v-html="warningMessage"></span>
                                </template>
                            </v-checkbox>
                        </v-card-text>
                    </div>
                    <v-text-field
                        @click.stop
                        v-model="search"
                        outlined
                        dense
                        :placeholder="placeholder"
                        single-line
                        hide-details
                        class="pa-2"
                        ref="searchbox"
                        @keypress.enter="onEnterKey"
                    ></v-text-field>
                    <v-divider />
                    <div class="scrollbox2 my-2" style="min-height: 400px; max-height: 400px" :style="{ 'max-width': icon ? '680px' : '780px' }">
                        <v-list>
                            <v-list-item-group>
                                <v-list-item link v-for="item in filteredItems" :key="item.id" :value="item.id" @click="accept(item.id)">
                                    <v-list-item-icon v-if="item.icon">
                                        <v-icon size="32">{{ item.icon }}</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title v-if="item.code" class="font-weight-bold">{{ item.code }} - {{ item.name }}</v-list-item-title>
                                        <v-list-item-title v-else class="font-weight-bold">
                                            <v-icon v-if="item.hasIssues" small color="orange darken-2" class="mr-2">mdi-alert</v-icon>
                                            {{ item.name }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle v-if="item.account || item.description" class="d-flex align-center justify-space-between">
                                            <span>{{ item.account || item.description }}</span>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>

                                    <!-- FIRST PILL -->
                                    <v-list-item-action v-if="type === 'product' && item.routeCount && item.routeCount === 1">
                                        <span class="text-caption green darken-1 white--text rounded py-1 px-2 font-weight-bold">1 RUTA</span>
                                    </v-list-item-action>
                                    <v-list-item-action v-else-if="type === 'product' && item.routeCount && item.routeCount > 1">
                                        <span class="text-caption green darken-1 white--text rounded py-1 px-2 font-weight-bold">{{ item.routeCount }} RUTAS</span>
                                    </v-list-item-action>
                                    <v-list-item-action v-else-if="type === 'product'">
                                        <span class="text-caption red darken-3 white--text rounded py-1 px-2 font-weight-bold">SIN RUTAS</span>
                                    </v-list-item-action>
                                    <v-list-item-action v-else-if="type === 'agency' && !item.active">
                                        <span class="text-caption red darken-3 white--text rounded py-1 px-2 font-weight-bold">INACTIVA</span>
                                    </v-list-item-action>

                                    <!-- SECOND PILL -->
                                    <v-list-item-action v-if="type === 'guide'">
                                        <span v-if="item.official" class="rounded grey white--text darken-3 px-2 text-caption">OFICIAL</span>
                                    </v-list-item-action>
                                    <v-list-item-action v-else-if="type === 'agency'">
                                        <span class="text-caption grey darken-3 white--text rounded py-1 px-2 font-weight-bold">{{ item.type }}</span>
                                    </v-list-item-action>
                                    <v-list-item-action v-else-if="type === 'product' || type === 'meeting-point'">
                                        <span class="text-caption grey darken-3 white--text rounded py-1 px-2 font-weight-bold">{{ item.zone }}</span>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>

                        <div v-if="filteredItems.length === 0" class="d-flex align-center justify-center grey--text">
                            <v-icon>mdi-information-outline</v-icon>
                            <span class="ml-2">No se han encontrado resultados</span>
                        </div>
                        <div v-else-if="filteredItems.length === 1" class="d-flex align-center justify-center grey--text">
                            <v-icon>mdi-keyboard-return</v-icon>
                            <span class="ml-2">Puedes pulsar ENTER para aceptar este elemento</span>
                        </div>
                    </div>
                </v-card-text>
            </div>
        </v-card>
    </v-dialog>
</template>

<style scoped lang="scss"></style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { SelectionDialogRequest, DataClient } from "@/services/DataClient";
import { Utilities } from "@/services/Utilities";
import { Component, Vue } from "vue-property-decorator";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";
import { VBtn, VCard, VDialog } from "vuetify/lib";

interface InternalItem {
    id: string;
    name: string;
    code?: string;
    description?: string;
    account?: string;
    active?: boolean;

    // generic ordering
    order?: number;

    // organization specific
    organizationId?: string;
    showInSales?: boolean;
    showInPurchases?: boolean;

    // guide specific
    official?: boolean;

    // agency specific
    type?: string;

    // product specific
    zone?: string;
    routeCount?: number;

    // issues
    hasIssues?: boolean;

    // icon
    icon?: string;
}

@Component
export default class extends Vue {
    dialog = false;

    type = "";
    organizationId = "";
    onlyActive = false;
    filter = "";
    filterIds: string[] = [];
    search = "";

    title = "";
    message = "";
    warningMessage = "";
    warningMessageChecked = false;
    icon = "";
    iconColor = "";

    items: InternalItem[] = [];

    get filteredItems(): InternalItem[] {
        let base: InternalItem[] = [];

        base = this.filterIds.length > 0 ? this.items.filter((z) => this.filterIds.includes(z.id)) : this.items;

        if (this.search) {
            return base.filter((z) => Utilities.multiwordFilterWithoutDiacritics(z, this.search, ["name", "code", "description", "account"]));
        } else {
            return base;
        }
    }

    get placeholder(): string {
        switch (this.type) {
            case "agency": {
                return "Buscar en las agencias ...";
            }
            case "organization": {
                return "Buscar en las organizaciones ...";
            }
            case "guide": {
                return "Buscar en los guías ...";
            }
            case "user": {
                return "Buscar en los usuarios ...";
            }
            case "employee": {
                return "Buscar en los empleados ...";
            }
            case "product": {
                return "Buscar en los productos ...";
            }
            case "carrier": {
                return "Buscar en los transportistas ...";
            }
            case "provider": {
                return "Buscar en los proveedores ...";
            }
            case "operator": {
                return "Buscar en los operadores ...";
            }
            case "meeting-point": {
                return "Buscar en los puntos de encuentro ...";
            }
            case "transfer-extra": {
                return "Buscar en los extras ...";
            }
            case "purchase-rate": {
                return "Buscar en las tarifas de compra ...";
            }
            default: {
                return "Buscar ...";
            }
        }
    }

    calculateTitle(): string {
        switch (this.type) {
            case "agency": {
                return "Seleccionar agencia";
            }
            case "organization": {
                return "Seleccionar organización";
            }
            case "employee": {
                return "Seleccionar empleado";
            }
            case "provider": {
                return "Seleccionar proveedor";
            }
            case "carrier": {
                return "Seleccionar transportista";
            }
            case "guide": {
                return "Seleccionar guía";
            }
            case "user": {
                return "Seleccionar usuario";
            }
            case "operator": {
                return "Seleccionar operador";
            }
            case "product": {
                return "Seleccionar producto";
            }
            case "meeting-point": {
                return "Seleccionar punto de encuentro";
            }
            case "transfer-extra": {
                return "Seleccionar extra";
            }
            case "purchase-rate": {
                return "Seleccionar tarifa de compra";
            }
            default: {
                return "Seleccionar";
            }
        }
    }

    mounted() {
        EventBus.$on(DataClient.Dialogs.OpenSelectionDialogEvent, this.handleOpenEvent);
    }

    beforeDestroy() {
        EventBus.$off(DataClient.Dialogs.OpenSelectionDialogEvent, this.handleOpenEvent);
    }

    reset() {
        this.dialog = false;
        this.type = "";
        this.filter = "";
        this.filterIds = [];
        this.search = "";
        this.onlyActive = false;

        this.title = "";
        this.organizationId = "";
        this.message = "";
        this.warningMessage = "";
        this.warningMessageChecked = false;
        this.icon = "";
        this.iconColor = "";
    }

    async handleOpenEvent(event: SelectionDialogRequest) {
        this.type = event.type;
        this.organizationId = event.organizationId || "";
        this.filter = event.filter || "";
        this.filterIds = event.filterIds || [];
        this.onlyActive = event.onlyActive || false;

        this.title = event.title || this.calculateTitle();
        this.message = event.message || "";
        this.warningMessage = event.warningMessage || "";
        this.icon = event.icon || "";
        this.iconColor = event.iconColor || "orange darken-2";

        await this.refresh();

        this.dialog = true;

        Utilities.focus(this, "searchbox");
    }

    accept(value: string) {
        if (this.warningMessage && !this.warningMessageChecked) {
            return;
        }

        DataClient.Dialogs.selection(value, true);
        this.reset();
    }

    cancel(fromEsc: boolean) {
        DataClient.Dialogs.selection("", true);
        this.reset();
    }

    async refresh() {
        switch (this.type) {
            case "employee": {
                this.items = await DataClient.Employees.getAll();
                break;
            }
            case "organization": {
                this.items = await DataClient.Organizations.getAll();
                break;
            }
            case "provider": {
                this.items = await DataClient.Providers.getAll();
                break;
            }
            case "carrier": {
                this.items = await DataClient.Carriers.getAll();
                break;
            }
            case "meeting-point": {
                const allMeetingPoints = await DataClient.MeetingPoints.getAll();

                this.items = [];
                for (const meetingPoint of allMeetingPoints) {
                    this.items.push({
                        id: meetingPoint.id,
                        code: "",
                        name: meetingPoint.name,
                        //description: meetingPoint.,
                        zone: meetingPoint.zoneName,
                    });
                }
                break;
            }
            case "product": {
                const allProducts = await DataClient.Products.getAll();

                this.items = [];
                for (const product of allProducts) {
                    if (this.filter && product.type !== this.filter) {
                        continue;
                    }

                    this.items.push({
                        id: product.id,
                        code: product.code,
                        name: product.name,
                        description: product.activityName,
                        zone: product.zoneName,
                        routeCount: product.routesCount || 0,
                    });
                }
                break;
            }
            case "agency": {
                const allAgencies = await DataClient.Agencies.getAll();

                this.items = [];
                for (const agency of allAgencies) {
                    if (this.filter && agency.type !== this.filter) {
                        continue;
                    }

                    if (this.onlyActive && !agency.active) {
                        continue;
                    }

                    let organizationNames: string[] = [];

                    let found = false;

                    for (const organization of agency.organizations) {
                        organizationNames.push(organization.organizationName);

                        if (organization.organizationId === this.organizationId) {
                            found = true;
                            break;
                        }
                    }

                    if (this.organizationId) {
                        if (!found) {
                            continue;
                        }
                    }

                    this.items.push({
                        id: agency.id,
                        name: agency.name,
                        //scription: (agency.account || "Sin cuenta") + " / " + (agency.seriesName || "Sin serie de facturación") + (agency.organizationId ? " / " + agency.organizationName : ""),
                        description: (agency.account || "Sin cuenta") + " / " + organizationNames.join(", "),
                        active: agency.active,
                        //description: agency.seriesName,
                    });

                    if (agency.type === "ttoo") {
                        this.items[this.items.length - 1].type = "TTOO";
                    } else if (agency.type === "pos") {
                        this.items[this.items.length - 1].type = "PUNTO DE VENTA";
                    } else if (agency.type === "ota") {
                        this.items[this.items.length - 1].type = "OTA";
                    } else if (agency.type === "direct") {
                        this.items[this.items.length - 1].type = "DIRECTO";
                    } else if (agency.type === "business") {
                        this.items[this.items.length - 1].type = "EMPRESA";
                    } else if (agency.type === "affiliate") {
                        this.items[this.items.length - 1].type = "AFILIADO";
                    }
                }
                break;
            }
            case "guide": {
                this.items = await DataClient.Guides.getAll();

                for (const item of this.items) {
                    if (item.name.startsWith("*")) {
                        item.official = true;
                        item.name = item.name.substr(1);
                    }
                }

                break;
            }
            case "user": {
                const allUsers = await DataClient.Users.getAll();

                this.items = [];
                for (const user of allUsers) {
                    if (this.filter && user.type !== this.filter) {
                        continue;
                    }

                    this.items.push({
                        id: user.id,
                        name: user.name,
                        description: user.username,
                    });
                }

                break;
            }
            case "operator": {
                this.items = await DataClient.Operators.getAll();
                break;
            }
            case "transfer-extra": {
                this.items = await DataClient.TransferExtras.getAll();
                break;
            }
            case "purchase-rate": {
                const allPurchaseRates = await DataClient.PurchaseRates.getAll();

                this.items = [];
                for (const rate of allPurchaseRates) {
                    if (this.filter && rate.parentId !== this.filter) {
                        continue;
                    }

                    this.items.push({
                        id: rate.id,
                        name: rate.name,
                        code: rate.code,
                    });
                }

                break;
            }
            default: {
                this.items = [];
            }
        }
    }

    onEnterKey() {
        const items = this.filteredItems;
        if (items.length === 1) {
            this.accept(items[0].id);
        }
    }
}
</script>
